import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import StatusPill from "ui-components/status-pill/statusPill";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import TerminalCard from "ui-components/terminal-card/terminalCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import { DataTableColumnOption } from "ui-components/data-table/dataTableTypes";
import { DockEmployeeRow } from "./dockEmployeeRowTypes";
import { ScanCountTags, SessionDurationCell } from "./dockScanLogColumns";

const employeeHelper = createColumnHelper<DockEmployeeRow>();

export const DOCK_EMPLOYEE_COLUMN_OPTIONS: DataTableColumnOption[] = [
  { id: "name", label: "Employee" },
  { id: "email", label: "Email" },
  { id: "tags", label: "Tags" },
  { id: "accountStatus", label: "Account Status" },
  { id: "dockPresence", label: "Dock Presence" },
  { id: "equipmentId", label: "Equipment" },
  { id: "sessionDuration", label: "Session Duration" },
  { id: "sessionScans", label: "Session Scans" },
  { id: "lastScanAt", label: "Last Scan" },
  { id: "lastLoginAt", label: "Last Login" },
  { id: "workflow", label: "Active Workflow" },
  { id: "terminalNumber", label: "Home Terminal" },
  { id: "createdAt", label: "Created" },
  { id: "updatedAt", label: "Updated" },
];

export const DOCK_EMPLOYEE_COLUMNS_HIDDEN_BY_DEFAULT = [
  "email",
  "terminalNumber",
  "createdAt",
  "updatedAt",
  "workflow",
];

export function getDockEmployeeColumns() {
  return [
    employeeHelper.accessor("firstName", {
      id: "name",
      header: "Employee",
      cell: (props) => {
        const row = props.row.original;
        return (
          <DockUserCard
            employeeId={row.employeeId}
            firstName={row.firstName}
            lastName={row.lastName}
            email={row.email}
            status={row.status}
            tags={row.tags}
            terminalNumber={row.terminalNumber}
            isOnDock={row.isOnDock}
            updatedAt={row.updatedAt}
            session={
              row.equipmentId
                ? {
                    equipmentId: row.equipmentId,
                    sessionDurationMs: row.sessionDurationMs,
                    scansTotal: row.scansTotal,
                    scansFailure: row.scansFailure,
                  }
                : undefined
            }
            keepTooltipInside={false}
          />
        );
      },
      size: 200,
      minSize: 180,
    }),
    employeeHelper.accessor("email", {
      id: "email",
      header: "Email",
      size: 200,
      minSize: 160,
    }),
    employeeHelper.accessor("tags", {
      id: "tags",
      header: "Tags",
      cell: (props) => (
        <div className="dock-users__tags">
          {props.getValue()?.map((tag) => (
            <span key={tag.id} className="dock-users__tag-badge">
              {tag.name}
            </span>
          ))}
        </div>
      ),
      size: 160,
      minSize: 120,
    }),
    employeeHelper.accessor("status", {
      id: "accountStatus",
      header: "Account Status",
      cell: (props) => (
        <StatusPill
          label={props.getValue()}
          variant={props.getValue() === "ACTIVE" ? "success" : "inactive"}
        />
      ),
      size: 120,
      minSize: 110,
    }),
    employeeHelper.accessor("isOnDock", {
      id: "dockPresence",
      header: "Dock Presence",
      cell: (props) => (
        <StatusPill
          label={props.getValue() ? "ON DOCK" : "OFF DOCK"}
          variant={props.getValue() ? "live" : "neutral"}
        />
      ),
      size: 120,
      minSize: 110,
    }),
    employeeHelper.accessor("equipmentId", {
      id: "equipmentId",
      header: "Equipment",
      cell: (props) => {
        const id = props.getValue();
        if (!id) return "—";
        return <EquipmentCard unitNumber={id} keepTooltipInside={false} />;
      },
      size: 120,
      minSize: 100,
    }),
    employeeHelper.accessor("sessionDurationMs", {
      id: "sessionDuration",
      header: "Session Duration",
      cell: (props) => (
        <SessionDurationCell
          ms={props.getValue()}
          startedAt={props.row.original.assignmentStartedAt}
        />
      ),
      size: 140,
      minSize: 120,
    }),
    employeeHelper.accessor("scansTotal", {
      id: "sessionScans",
      header: "Session Scans",
      cell: (props) => (
        <ScanCountTags
          total={props.row.original.scansTotal}
          success={props.row.original.scansSuccess}
          failure={props.row.original.scansFailure}
        />
      ),
      size: 200,
      minSize: 170,
    }),
    employeeHelper.accessor("lastScanAt", {
      id: "lastScanAt",
      header: "Last Scan",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="relative" />,
      size: 120,
      minSize: 110,
    }),
    employeeHelper.accessor("lastLoginAt", {
      id: "lastLoginAt",
      header: "Last Login",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="relative" />,
      size: 120,
      minSize: 110,
    }),
    employeeHelper.accessor("activeWorkflow", {
      id: "workflow",
      header: "Active Workflow",
      cell: (props) => {
        const wf = props.getValue();
        if (!wf) return "—";
        return (
          <div>
            <span className="dock-users__workflow-badge">
              {wf.workflowType?.replace(/_/g, " ")}
            </span>
            <span className="text-light xgs-table__cell-subitem">
              {wf.workflowState?.replace(/_/g, " ")}
            </span>
          </div>
        );
      },
      size: 160,
      minSize: 140,
    }),
    employeeHelper.accessor("terminalNumber", {
      id: "terminalNumber",
      header: "Home Terminal",
      cell: (props) => {
        const terminalNumber = props.getValue();
        if (terminalNumber == null) return "—";
        return (
          <TerminalCard terminalNumber={terminalNumber} keepTooltipInside={false} />
        );
      },
      size: 180,
      minSize: 160,
    }),
    employeeHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Created",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="local" />,
      size: 130,
      minSize: 120,
    }),
    employeeHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      cell: (props) => <TimestampDisplay value={props.getValue()} format="relative" />,
      size: 120,
      minSize: 110,
    }),
  ];
}
